import { useMemo, useState } from 'react';
import { templateFeatureLabel } from '@/components/publish-flow/templateOptions';
import type { GalleryTemplate } from '@/modules/templateScenes';

export type TemplateDifficultyFilter = 'all' | 'beginner' | 'intermediate' | 'advanced';
export type TemplateSortOrder = 'rank' | 'asc' | 'desc' | 'newest' | 'oldest';

const TEMPLATE_PAGE_SIZE = 4;

const acceptedTime = (template: GalleryTemplate) => (template.acceptedAt ? Date.parse(template.acceptedAt) : 0);

export function useTemplateGallery(templates: GalleryTemplate[]) {
  const [searchQuery, setSearchQuery] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState<TemplateDifficultyFilter>('all');
  const [featureFilter, setFeatureFilter] = useState<string>('all');
  const [sortOrder, setSortOrder] = useState<TemplateSortOrder>('rank');
  const [currentPage, setCurrentPage] = useState(1);

  const filteredAndSorted = useMemo(() => {
    let filtered = [...templates];

    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      filtered = filtered.filter(
        (template) =>
          template.title.toLowerCase().includes(query) ||
          template.description.toLowerCase().includes(query) ||
          template.category.toLowerCase().includes(query) ||
          template.tags.some((tag) => tag.toLowerCase().includes(query)) ||
          (template.features ?? []).some((key) => templateFeatureLabel(key).toLowerCase().includes(query))
      );
    }

    if (difficultyFilter !== 'all') {
      filtered = filtered.filter((template) => template.difficulty === difficultyFilter);
    }

    if (featureFilter !== 'all') {
      filtered = filtered.filter((template) => (template.features ?? []).includes(featureFilter));
    }

    filtered.sort((a, b) => {
      switch (sortOrder) {
        case 'asc':
          return a.title.localeCompare(b.title);
        case 'desc':
          return b.title.localeCompare(a.title);
        case 'oldest':
          return acceptedTime(a) - acceptedTime(b) || a.title.localeCompare(b.title);
        case 'newest':
          return acceptedTime(b) - acceptedTime(a) || a.title.localeCompare(b.title);
        case 'rank':
        default:
          return (b.rating ?? 0) - (a.rating ?? 0) || b.id.localeCompare(a.id);
      }
    });

    return filtered;
  }, [templates, searchQuery, difficultyFilter, featureFilter, sortOrder]);

  const paginated = useMemo(() => {
    const start = (currentPage - 1) * TEMPLATE_PAGE_SIZE;
    return filteredAndSorted.slice(start, start + TEMPLATE_PAGE_SIZE);
  }, [filteredAndSorted, currentPage]);

  const totalPages = Math.ceil(filteredAndSorted.length / TEMPLATE_PAGE_SIZE);

  const [filters, setFilters] = useState({ searchQuery, difficultyFilter, featureFilter, sortOrder });
  if (
    filters.searchQuery !== searchQuery ||
    filters.difficultyFilter !== difficultyFilter ||
    filters.featureFilter !== featureFilter ||
    filters.sortOrder !== sortOrder
  ) {
    setFilters({ searchQuery, difficultyFilter, featureFilter, sortOrder });
    setCurrentPage(1);
  }

  return {
    templates: paginated,
    searchQuery,
    setSearchQuery,
    difficultyFilter,
    setDifficultyFilter,
    featureFilter,
    setFeatureFilter,
    sortOrder,
    setSortOrder,
    currentPage,
    setCurrentPage,
    totalPages,
  };
}
